// These configurations cannot be changed by the end user / dev team only

import {StaticConfigs} from '../app/models/configs/static-configs';
import {secureProtocol} from '../app/constants/server/secure-protocol.constant';
import {Style} from '@capacitor/status-bar';
import {InteractionType} from '@azure/msal-browser';
import {IMAGE_PATH, SVG_ICON_PATH} from '../app/constants/image-paths.constants';
import {IconSize, TagType} from '../app/bfa-api/models';
import {SupportedLocalesEnum} from '../app/enums/supported-locales.enum';

export const STATIC_CONFIGS: StaticConfigs = {
    localStorageName: 'aptera',
    language: {
        supportedLocaleNames: [
            SupportedLocalesEnum.DeDe,
            SupportedLocalesEnum.EnGb,
            SupportedLocalesEnum.EnUs
        ],
        defaultLocaleName: SupportedLocalesEnum.EnGb
    },
    servers: {
        dev: {
            url: secureProtocol + 'app-dev.amagno.cloud/amagnome/api',
            displayUrl: 'app-dev.amagno.cloud',
            name: 'Amagno Business Cloud (Dev)'
        },
        test: {
            url: secureProtocol + 'app-test.amagno.cloud/amagnome/api',
            displayUrl: 'app-test.amagno.cloud',
            name: 'Amagno Business Cloud (Test)'
        },
        prod: {
            url: secureProtocol + 'amagno.cloud/amagnome/api',
            displayUrl: 'amagno.cloud',
            name: 'Amagno Business Cloud (DE)'
        },
        prodch: {
            url: secureProtocol + 'cloud.amagno.ch/amagnome/api',
            displayUrl: 'cloud.amagno.ch',
            name: 'Amagno Business Cloud (CH)'
        },
    },
    sentry: {
        dsn: 'https://67e9b9b42057472ca2aa63d1de8c4872@o4505035118411776.ingest.sentry.io/4505068471320576',
        tracePropagationTargets: [
            'localhost',
            'amagno.app',
            'amagno-app-dev.azurewebsites.net',
            'amagno-app-test.azurewebsites.net',
            'amagno-app-staging.azurewebsites.net',
            'amagno.cloud',
            'app-test.amagno.cloud',
            'app-dev.amagno.cloud',
        ],
    },
    msal: {
        azureLoginActive: true,
        // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
        clientId: '0de6048b-41c1-4807-b85c-c122383665c0',
        // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
        authority: 'https://login.microsoftonline.com/common',
        // Must be the same redirectUri as what was provided in your app registration.
        redirectUri: '/',
        postLogoutRedirectUri: '/auth/login',
        capacitorLocalStorageKey: 'MSAL_CAPACITOR_TOKEN_REQUEST_DATA',
        iosRedirectUri: 'https://web.amagno.app', // this needs to be a valid url - otherwise the redirect from ms login doesn't work - could be any url, but we use one of our own
        androidRedirectUri: 'https://localhost',
        silentLoginUri: 'https://login.microsoftonline.com/%TENANT_ID%/oauth2/v2.0/token',
        tokenUri: 'https://login.microsoftonline.com/organizations/oauth2/v2.0/token',
        codeUri: 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize',
        protectedResourceMap: [
            [
                'https://graph.microsoft.com/v1.0/me',
                ['user.read']
            ]
        ],
        scopes: ['user.read'],
        interactionType: InteractionType.Redirect,
        browserCacheLocation: 'localStorage',
    },
    partner: [
        {
            name: IMAGE_PATH + '/' + 'dms@cloud',
            id: '1',
            logo: 'dms_at_cloud.svg',
        }
    ],
    maxInputCharSize: 4000,
    ui: {
        input: {
            validationPattern: {
                number: /^-?\d+(\.\d+(,\d+)?|,\d+(\.\d+)?)?$/,
                vaultName: /^[^\\"<>|\0-\x1f:*?/]+$/,
                magnetName: /^[^\\"<>|\0-\x1f:*?/]+$/,
            },
            suggestions: {
                defaultCount: 10,
                defaultOffset: 0
            }
        },
        defaultLoginLogo: {
            url: SVG_ICON_PATH + '/' + 'amagno_logo.svg',
            alt: 'Amagno Logo',
        },
        defaultFindSpotMatchColors: [
            'hsl(352 80% 74%)',
            'hsl(142, 35%, 70%)',
            'hsl(318, 45%, 80%)',
            'hsl(207, 63%, 69%)'
        ],
        breakpoints: {
            twoColumnMinWidth: 740,
            portraitModeDocumentPreviewOneColumnMaxWidth: 1025,
            twoColumnMinHeight: 600
        },
        panel: {
            minHeightInPx: 200,
            minWidthInPx: 250,
            collapsedMinOuterWidthInPx: 380,
            wideCollapsedBarMinOuterWidthInPx: 390,
            collapseAnimationDurationInMs: 250,
        },
        tags: {
            editModeAllowedTagTypeList: new Set([
                TagType.Date,
                TagType.Number,
                TagType.SingleLineString,
                TagType.DocumentName,
                TagType.UserSelection,
                TagType.Selection,
            ]),
            recentValueTagTypes: new Set([
                TagType.Date,
                TagType.Number,
                TagType.SingleLineString,
            ]),
            clickFillTagTypeList: new Set([
                TagType.Date,
                TagType.DocumentName,
                TagType.Number,
                TagType.SingleLineString,
                TagType.Selection,
                TagType.UserSelection,
            ])
        },
        tagPanelBreakPointWidth: 700,
        minListItemsForListFilter: 3,
        defaultIconFetchSize: IconSize.Large,
        backgroundsByMonth: {
            light: [
                'login.background.01.webp',
                'login.background.02.webp',
                'login.background.03.webp',
                'login.background.04.webp',
                'login.background.05.webp',
                'login.background.06.webp',
                'login.background.07.webp',
                'login.background.08.webp',
                'login.background.09.webp',
                'login.background.10.webp',
                'login.background.11.webp',
                'login.background.12.webp'
            ],
            dark: [
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp',
                'login.background.dark.webp'
            ]
        },
        androidSystemBars: {
            light: {
                backgroundColor: '#ffffff',
                statusBarStyle: Style.Light
            },
            dark: {
                backgroundColor: '#000000',
                statusBarStyle: Style.Dark
            }
        }
    },
};
